// Page_Formacion
//
// Pagina de Formacion del portal K12: videos, guias PDF e informacion de uso
// de la plataforma ("primeros pasos"). La misma pagina sirve al profesor
// (/portal/teacher/formacion) y a la institucion (/portal/inst/formacion):
// solo cambia la audiencia con la que se consultan los recursos.
//
// El contenido lo gestiona el admin en /admin/formacion.
// Toda la UI pasa por i18n (namespace "training"), a diferencia de la pagina
// equivalente de REVI, que esta en espanol fijo.

import React, { useMemo, useRef } from "react";
import {
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Pagination,
  Result,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  FilePdfOutlined,
  LeftOutlined,
  PlayCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { PageHeader, BrandThemeProvider } from "../../../ui";
import { useFormacionController } from "./useFormacionController";

import styles from "./Page_Formacion.module.scss";

/**
 * Convierte una URL de YouTube o Vimeo en su forma embebible.
 * Cualquier otra URL devuelve null y se ofrece abrirla en pestana nueva.
 */
export function getVideoEmbedUrl(rawUrl) {
  if (!rawUrl) return null;

  const ytMatch =
    rawUrl.match(/youtube\.com\/watch\?v=([^&]+)/i) ||
    rawUrl.match(/youtu\.be\/([^?&]+)/i);
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  const vimeoMatch = rawUrl.match(/vimeo\.com\/(\d+)/i);
  if (vimeoMatch?.[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return null;
}

function ResourceStrip({ items, loading, error, onOpen, kind, t }) {
  const viewportRef = useRef(null);

  const scrollBy = (delta) => {
    if (!viewportRef.current) return;
    viewportRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (loading) return <Skeleton active paragraph={{ rows: 3 }} />;

  if (error) {
    return <Result status="error" title={t("errors.load")} subTitle={error} />;
  }

  if (!items?.length) {
    return <Empty description={t("empty.results")} />;
  }

  return (
    <div className={styles.strip}>
      <Button
        aria-label={t("scroll.left")}
        icon={<LeftOutlined />}
        onClick={() => scrollBy(-420)}
      />
      <div ref={viewportRef} className={styles.stripViewport} role="list">
        {items.map((it) => (
          <Card
            key={it.id}
            hoverable
            className={styles.resourceCard}
            onClick={() => onOpen(kind, it)}
            cover={
              it.image ? (
                <img className={styles.cover} alt={it.title} src={it.image} />
              ) : (
                <div className={`${styles.cover} ${styles.coverPlaceholder}`}>
                  {kind === "video" ? (
                    <PlayCircleOutlined />
                  ) : (
                    <FilePdfOutlined />
                  )}
                </div>
              )
            }
          >
            <div className={styles.cardMeta}>
              <Tooltip title={it.title}>
                <Typography.Text strong ellipsis>
                  {it.title}
                </Typography.Text>
              </Tooltip>

              <div>
                <Tag
                  icon={
                    kind === "video" ? (
                      <PlayCircleOutlined />
                    ) : (
                      <FilePdfOutlined />
                    )
                  }
                >
                  {kind === "video" ? t("tags.video") : t("tags.pdf")}
                </Tag>
              </div>

              <Typography.Paragraph
                className={styles.cardDesc}
                ellipsis={{ rows: 2, tooltip: it.description }}
              >
                {it.description}
              </Typography.Paragraph>
            </div>
          </Card>
        ))}
      </div>
      <Button
        aria-label={t("scroll.right")}
        icon={<RightOutlined />}
        onClick={() => scrollBy(420)}
      />
    </div>
  );
}

export function Page_Formacion({ audience = "teacher" }) {
  const { t } = useTranslation("training");
  const {
    searchInput,
    videos,
    pdfs,
    info,
    videoPage,
    pdfPage,
    preview,
    actions,
  } = useFormacionController(audience);

  const videoPagination = videos.pagination;
  const pdfPagination = pdfs.pagination;
  const previewTitle = preview?.item?.title || "";

  const videoEmbedUrl = useMemo(() => {
    if (preview.type !== "video") return null;
    return getVideoEmbedUrl(preview.item?.url);
  }, [preview.type, preview.item]);

  const subtitle =
    audience === "institution"
      ? t("subtitle.institution")
      : t("subtitle.teacher");

  return (
    <BrandThemeProvider>
      <div className={styles.page}>
        <PageHeader title={t("title")} description={subtitle} />

        <Card className={styles.search} size="small">
          <Input.Search
            value={searchInput}
            onChange={(e) => actions.setSearchInput(e.target.value)}
            placeholder={t("searchPlaceholder")}
            allowClear
          />
        </Card>

        <div className={styles.carousels}>
          <Card
            title={t("sections.videos")}
            size="small"
            extra={
              videoPagination ? (
                <Typography.Text type="secondary">
                  {t("results", { count: videoPagination.totalDocs })}
                </Typography.Text>
              ) : null
            }
          >
            <ResourceStrip
              items={videos.items}
              loading={videos.loading}
              error={videos.error}
              onOpen={actions.openPreview}
              kind="video"
              t={t}
            />

            {videoPagination && videoPagination.totalPages > 1 ? (
              <div className={styles.pagination}>
                <Pagination
                  current={videoPage}
                  pageSize={videoPagination.limit}
                  total={videoPagination.totalDocs}
                  onChange={(p) => actions.setVideoPage(p)}
                  showSizeChanger={false}
                />
              </div>
            ) : null}
          </Card>

          <Card
            title={t("sections.pdfs")}
            size="small"
            extra={
              pdfPagination ? (
                <Typography.Text type="secondary">
                  {t("results", { count: pdfPagination.totalDocs })}
                </Typography.Text>
              ) : null
            }
          >
            <ResourceStrip
              items={pdfs.items}
              loading={pdfs.loading}
              error={pdfs.error}
              onOpen={actions.openPreview}
              kind="pdf"
              t={t}
            />

            {pdfPagination && pdfPagination.totalPages > 1 ? (
              <div className={styles.pagination}>
                <Pagination
                  current={pdfPage}
                  pageSize={pdfPagination.limit}
                  total={pdfPagination.totalDocs}
                  onChange={(p) => actions.setPdfPage(p)}
                  showSizeChanger={false}
                />
              </div>
            ) : null}
          </Card>
        </div>

        <Card title={t("sections.info")} size="small">
          {info.loading ? <Skeleton active paragraph={{ rows: 4 }} /> : null}

          {!info.loading && info.error ? (
            <Result
              status="error"
              title={t("errors.load")}
              subTitle={info.error}
            />
          ) : null}

          {!info.loading && !info.error && !info.item ? (
            <Empty description={t("empty.info")} />
          ) : null}

          {!info.loading && !info.error && info.item ? (
            <div className={styles.infoHero}>
              {info.item.image ? (
                <div className={styles.infoMedia}>
                  <img
                    className={styles.infoImg}
                    alt={info.item.title}
                    src={info.item.image}
                  />
                </div>
              ) : null}

              <div className={styles.infoContent}>
                <Typography.Title level={4} className={styles.infoTitle}>
                  {info.item.title}
                </Typography.Title>
                <Typography.Paragraph className={styles.infoDesc}>
                  {info.item.description}
                </Typography.Paragraph>
              </div>
            </div>
          ) : null}
        </Card>

        <Modal
          open={preview.open}
          title={previewTitle}
          onCancel={actions.closePreview}
          footer={null}
          width={980}
          destroyOnHidden
        >
          {preview.type === "video" ? (
            <div className={styles.preview}>
              {videoEmbedUrl ? (
                <iframe
                  className={styles.previewFrame}
                  src={videoEmbedUrl}
                  title={previewTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <Result
                  status="info"
                  title={t("preview.notEmbeddable")}
                  subTitle={t("preview.notEmbeddableHint")}
                  extra={
                    <Button
                      type="primary"
                      onClick={() =>
                        window.open(
                          preview.item?.url,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {t("preview.openVideo")}
                    </Button>
                  }
                />
              )}
            </div>
          ) : null}

          {preview.type === "pdf" ? (
            <div className={styles.preview}>
              <div className={styles.previewActions}>
                <Button
                  type="primary"
                  onClick={() =>
                    window.open(
                      preview.item?.file,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  {t("preview.openPdf")}
                </Button>
              </div>
              {preview.item?.file ? (
                <iframe
                  className={styles.previewFrame}
                  src={preview.item.file}
                  title={previewTitle}
                />
              ) : (
                <Empty description={t("empty.pdfFile")} />
              )}
            </div>
          ) : null}
        </Modal>
      </div>
    </BrandThemeProvider>
  );
}

/** Wrapper para el portal de la institucion (misma pagina, otra audiencia). */
/* eslint-disable react/jsx-pascal-case */
export function Page_Formacion_Institution() {
  return <Page_Formacion audience="institution" />;
}
/* eslint-enable react/jsx-pascal-case */
